import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/Header';
import { FullscreenIcon, CloseIcon, StarIcon, Rotate3DIcon } from '../components/Icons';
import { useNotification } from '../components/Notification';
import { Viewer360, ConfigEncoder } from '../modules';
import { getProduct } from '../firebase';
import { useLanguage } from '../contexts/LanguageContext';
import type { ProductConfig, VariantConfig, ColorVariant, CustomVariant } from '../types';
import { resolveText } from '../types';

// Try to get product from Service Worker cache
async function getProductFromCache(productId: string): Promise<ProductConfig | null> {
  try {
    const cache = await caches.open('product-data');
    const response = await cache.match(`/offline-product/${productId}`);
    if (response) {
      return await response.json();
    }
  } catch {
    // Cache not available or error
  }
  return null;
}

export default function ProductPage() {
  const { productId } = useParams<{ productId?: string }>();
  const { language, t } = useLanguage();
  const [config, setConfig] = useState<ProductConfig | null>(null);
  const [variants, setVariants] = useState<VariantConfig[]>([]);
  const [activeColorId, setActiveColorId] = useState<string>('');
  const [activeCustomId, setActiveCustomId] = useState<string>('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);

  const viewerRef = useRef<Viewer360 | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerSectionRef = useRef<HTMLDivElement>(null);

  const { NotificationComponent } = useNotification();

  // Get effective variants with defaults
  const colorVariants = config ? ConfigEncoder.getColorVariants(config) : [];
  const customVariants = config ? ConfigEncoder.getCustomVariants(config) : [];

  // Get active variants for specs display
  const activeCustomVariant = customVariants.find(c => c.id === activeCustomId);
  const activeColorVariant = colorVariants.find(c => c.id === activeColorId);

  // Combined specs: fixed specs + color specs + custom variant specs
  const combinedSpecs = useMemo(() => {
    const fixedSpecs = config?.specs || [];
    const colorSpecs = activeColorVariant?.specs || [];
    const variantSpecs = activeCustomVariant?.specs || [];
    return [...fixedSpecs, ...colorSpecs, ...variantSpecs];
  }, [config?.specs, activeColorVariant?.specs, activeCustomVariant?.specs]);

  // Check if there are any specs to display
  const hasSpecs = combinedSpecs.length > 0;

  // Check if a color is rotatable (not gallery mode)
  // Note: customId parameter kept for API compatibility but not used
  const isRotatable = useCallback((colorId: string, _customId?: string): boolean => {
    if (!config) return false;
    const override = config.variantOverrides?.[colorId];
    // Check override first, then global setting
    if (override?.galleryMode !== undefined) {
      return !override.galleryMode;
    }
    return !config.galleryMode;
  }, [config]);

  // Specs title (backward compat: check customVariantsTitle too)
  const specsTitle = config?.specsTitle || (config as any)?.customVariantsTitle || '';

  // Replace template variables
  const replaceVariables = useCallback((text: string) => {
    if (!config) return text;
    const productName = resolveText(config.name, language);
    const colorName = activeColorVariant ? resolveText(activeColorVariant.name, language) : '';
    const customName = activeCustomVariant ? resolveText(activeCustomVariant.name, language) : '';
    return text
      .replace(/\{urun\}/g, productName)
      .replace(/\{renk\}/g, colorName)
      .replace(/\{varyant\}/g, customName)
      .replace(/\{link\}/g, window.location.href);
  }, [config, activeColorVariant, activeCustomVariant, language]);

  // Process description with variables
  const processedDescription = useMemo(() => {
    if (!config?.description) return '';
    const desc = resolveText(config.description, language);
    return replaceVariables(desc);
  }, [config?.description, language, replaceVariables]);

  // SEO: Sayfa başlığını ürün adına göre güncelle
  useEffect(() => {
    if (config?.name) {
      const productName = resolveText(config.name, language);
      document.title = `${productName} - Sibuzhome`;
    }
    return () => {
      document.title = 'Sibuzhome | 360° Görüntüleyici';
    };
  }, [config?.name, language]);

  useEffect(() => {
    async function loadProduct() {
      setLoading(true);

      const searchParams = new URLSearchParams(window.location.search);
      const idFromQuery = searchParams.get('id');
      const finalProductId = productId || idFromQuery;

      if (finalProductId) {
        // Try cache first, then Firebase
        let product: ProductConfig | null = null;

        // 1. Try to get from cache (for offline support)
        product = await getProductFromCache(finalProductId);

        // 2. If not in cache, try Firebase
        if (!product) {
          try {
            product = await getProduct(finalProductId);
          } catch {
            // Firebase failed, might be offline
          }
        }

        if (product) {
          setConfig(product);
          setVariants(ConfigEncoder.toViewerVariants(product));

          // Set initial color and custom
          const initialColor = ConfigEncoder.getInitialColor(product);
          const initialCustom = ConfigEncoder.getInitialCustom(product);
          setActiveColorId(initialColor.id);
          setActiveCustomId(initialCustom.id);

          setLoading(false);
          return;
        }
      } else {
        const urlConfig = ConfigEncoder.getFromURL();
        if (urlConfig) {
          setConfig(urlConfig);
          setVariants(ConfigEncoder.toViewerVariants(urlConfig));

          const initialColor = ConfigEncoder.getInitialColor(urlConfig);
          const initialCustom = ConfigEncoder.getInitialCustom(urlConfig);
          setActiveColorId(initialColor.id);
          setActiveCustomId(initialCustom.id);

          setLoading(false);
          return;
        }
      }

      window.location.href = 'https://sibuzhome.com';
    }

    loadProduct();
  }, [productId]);

  useEffect(() => {
    if (loading || !containerRef.current || variants.length === 0) return;

    if (viewerRef.current) {
      viewerRef.current.destroy();
    }

    const initialVariantId = ConfigEncoder.getVariantId(activeColorId, activeCustomId);

    viewerRef.current = new Viewer360({
      container: containerRef.current,
      variants: variants,
      initialVariant: initialVariantId,
      sensitivity: 60,
      enableTouch: true,
      enableMouse: true,
      preloadFrames: 4,
      wrapAround: config?.wrapAround !== false,
      dragHintText: t('product.dragHint'),
      loadingText: t('viewer.loading'),
      onVariantChange: (variantId) => {
        const { colorId, customId } = ConfigEncoder.parseVariantId(variantId);
        setActiveColorId(colorId);
        setActiveCustomId(customId);
      },
    });

    return () => {
      viewerRef.current?.destroy();
    };
  }, [loading, variants]);

  // Dil değişince viewer içindeki metinleri güncelle (viewer'ı yeniden oluşturmadan)
  useEffect(() => {
    viewerRef.current?.setTexts({
      dragHint: t('product.dragHint'),
      loading: t('viewer.loading'),
    });
  }, [language]);

  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (!viewerRef.current) return;

      switch (e.key) {
        case 'ArrowLeft':
          viewerRef.current.goToFrame(viewerRef.current.getCurrentFrame() - 1);
          document.querySelector('.viewer-360')?.classList.add('hide-hint');
          break;
        case 'ArrowRight':
          viewerRef.current.goToFrame(viewerRef.current.getCurrentFrame() + 1);
          document.querySelector('.viewer-360')?.classList.add('hide-hint');
          break;
        case 'f':
        case 'F':
          toggleFullscreen();
          break;
        case 'Escape':
          if (isFullscreen) toggleFullscreen();
          break;
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [isFullscreen]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => {
      const newValue = !prev;
      document.body.style.overflow = newValue ? 'hidden' : '';

      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 50);

      return newValue;
    });
  }, []);

  const handleColorClick = (color: ColorVariant) => {
    if (color.isDisabled) return;
    if (viewerRef.current) {
      viewerRef.current.switchColor(color.id);
    }
  };

  const handleCustomClick = (custom: CustomVariant) => {
    if (custom.isDisabled) return;
    if (viewerRef.current) {
      viewerRef.current.switchCustom(custom.id);
    }
  };

  const handleCTAClick = useCallback(() => {
    if (!config?.cta) return;

    const { action, value, emailSubject, messageTemplate } = config.cta;

    const defaultTemplate = t('cta.defaultMessage');
    const template = messageTemplate ? resolveText(messageTemplate, language) : defaultTemplate;
    const finalMessage = replaceVariables(template);

    switch (action) {
      case 'link':
      case 'link_blank':
        window.open(value, '_blank', 'noopener,noreferrer');
        break;
      case 'email': {
        const defaultSubject = t('cta.defaultSubject');
        const subjectTemplate = emailSubject ? resolveText(emailSubject, language) : defaultSubject;
        const subject = encodeURIComponent(replaceVariables(subjectTemplate));
        const body = encodeURIComponent(finalMessage);
        window.location.href = `mailto:${value}?subject=${subject}&body=${body}`;
        break;
      }
      case 'whatsapp': {
        const phone = value.replace(/\D/g, '');
        window.open(`https://wa.me/${phone}?text=${encodeURIComponent(finalMessage)}`, '_blank', 'noopener,noreferrer');
        break;
      }
    }
  }, [config, replaceVariables, t, language]);

  if (loading || !config) {
    return (
      <div className="loader-page">
        <div className="loader">
          <div className="loader-spinner"></div>
          <div className="loader-text">{t('product.loading')}</div>
        </div>
        <style>{`
          .loader-page {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f5f5f5;
          }
          .loader {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 16px;
          }
          .loader-spinner {
            width: 40px;
            height: 40px;
            border: 3px solid #e8e8e8;
            border-top-color: #e6a532;
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
          }
          .loader-text {
            color: #737373;
            font-size: 14px;
          }
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  // Check if we should show color/custom selectors (more than 1 or not default)
  const showColorSelector = colorVariants.length > 1 || colorVariants[0]?.id !== 'default';
  const showCustomSelector = customVariants.length > 1 || customVariants[0]?.id !== 'default';

  return (
    <div id="app">
      <Header />

      <main className="main">
        <div className="hero">
          <div
            ref={viewerSectionRef}
            className={`viewer-section ${isFullscreen ? 'fullscreen' : ''}`}
          >
            <div className="viewer-container">
              <div ref={containerRef} id="viewerContainer"></div>
              <button className="btn-fullscreen" onClick={toggleFullscreen}>
                <FullscreenIcon />
              </button>
            </div>

            <button className="btn-fullscreen-close" onClick={toggleFullscreen}>
              <CloseIcon />
            </button>
          </div>

          <div className="product-section">
            {config.badge && (
              <div className="product-badge">
                <StarIcon />
                <span>{resolveText(config.badge, language)}</span>
              </div>
            )}

            <h1 className="product-title">{resolveText(config.name, language)}</h1>

            {/* Color Variants - Ürün isminin hemen altında */}
            {showColorSelector && (
              <div className="product-variants">
                <div className="product-variants-label">{t('product.colorOptions')}</div>
                <div className="product-variants-grid">
                  {colorVariants.map((color) => (
                    <button
                      key={color.id}
                      className={`product-variant-btn ${activeColorId === color.id ? 'active' : ''} ${color.isDisabled ? 'disabled' : ''}`}
                      onClick={() => handleColorClick(color)}
                      disabled={color.isDisabled}
                    >
                      <span
                        className="variant-swatch"
                        style={color.pattern
                          ? { backgroundImage: `url(${color.pattern})`, backgroundSize: 'cover' }
                          : { backgroundColor: color.color }
                        }
                      />
                      <span className="variant-name">{resolveText(color.name, language)}</span>
                      {isRotatable(color.id, activeCustomId) && (
                        <span className="rotatable-badge"><Rotate3DIcon /></span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Custom Variants - Renk seçicinin altında */}
            {showCustomSelector && (
              <div className="product-variants">
                <div className="product-variants-label">{t('product.options')}</div>
                <div className="product-variants-grid">
                  {customVariants.map((custom) => (
                    <button
                      key={custom.id}
                      className={`product-variant-btn custom-btn ${activeCustomId === custom.id ? 'active' : ''} ${custom.isDisabled ? 'disabled' : ''}`}
                      onClick={() => handleCustomClick(custom)}
                      disabled={custom.isDisabled}
                    >
                      <span className="variant-name">{resolveText(custom.name, language)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {processedDescription && (
              <p className="product-desc">{processedDescription}</p>
            )}

            {/* Specs Card - Fixed specs + variant-specific specs */}
            {hasSpecs && (
              <div className="specs-card">
                <div className="specs-header">
                  <h3 className="specs-title">{resolveText(specsTitle, language) || t('product.specs')}</h3>
                </div>
                <div className="specs-list">
                  {combinedSpecs.map((spec, index) => (
                    <div key={index} className="spec-row">
                      <span className="spec-label">{resolveText(spec.label, language)}</span>
                      <span className="spec-value">{resolveText(spec.value, language)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {config.features && config.features.length > 0 && (
              <div className="features-list">
                {config.features.map((feature, index) => (
                  <span key={index} className="feature-tag">{resolveText(feature.text, language)}</span>
                ))}
              </div>
            )}

            {config.cta && (
              <button className="btn-primary" onClick={handleCTAClick}>
                {resolveText(config.cta.label, language)}
              </button>
            )}
          </div>
        </div>
      </main>

      <footer className="footer">
        © 2025 Sibuzhome | Tüm Haklar Saklıdır | <a href="https://flatdijital.com/" target="_blank" rel="noopener noreferrer">flat</a> tarafından hazırlanmıştır.
      </footer>

      {NotificationComponent}
    </div>
  );
}
